import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { productApi, Product } from '../../api/admin';
import {
  downloadExcelBuffer,
  styleExcelHeaderRow,
  todayFileStamp,
  writeLandscapePdfTable,
} from '../../utils/adminReportExport';

const ProductManagement: React.FC = () => {
  const [tab, setTab] = useState(0); // 0 = local, 1 = skyfin
  const [products, setProducts] = useState<Product[]>([]);
  const [skyfinProducts, setSkyfinProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<Partial<Product>>({
    productName: '',
    applicationFeeDescription: '',
    applicationFeePercentage: undefined,
    applicationFeeMinimum: undefined,
    applicationFeeMaximum: undefined,
    loanTenureDescription: '',
    loanTenureMonths: undefined,
    currency: '',
    interestRateDescription: '',
    interestRatePercentage: undefined,
    interestRateEffectiveDate: '',
    administrationFeesDescription: '',
    administrationFeesPercentage: undefined,
    serviceFeesDescription: '',
    serviceFeesAmount: undefined,
    minimumPrincipal: undefined,
    maximumPrincipal: undefined,
    startDate: '',
    closeDate: '',
    amortization: '',
    interestMethod: '',
    interestCalculationPeriod: '',
    charges: '',
    otherDetails: '',
    eligibleGender: '',
    isActive: true,
  });

  useEffect(() => {
    if (tab === 0) {
      loadProducts();
    } else {
      loadSkyfinProducts();
    }
  }, [tab]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const allProducts = await productApi.getAllProducts();
      setProducts(allProducts);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load local products');
    } finally {
      setLoading(false);
    }
  };

  const loadSkyfinProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const remote = await productApi.getSkyfinProducts();
      setSkyfinProducts(remote);
      if (!remote.length) {
        setSuccess('Connected to Skyfin, but no loan products were returned.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to load products from Skyfin. Check backend Fineract/Skyfin connectivity.'
      );
      setSkyfinProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromSkyfin = async () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      const result = await productApi.syncSkyfinProducts();
      setSuccess(result.message || `Synced ${result.count || 0} products from Skyfin`);
      setTab(0);
      await loadProducts();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to sync products from Skyfin'
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...product,
        interestRateEffectiveDate: product.interestRateEffectiveDate ? product.interestRateEffectiveDate.split('T')[0] : '',
        startDate: product.startDate ? product.startDate.split('T')[0] : '',
        closeDate: product.closeDate ? product.closeDate.split('T')[0] : '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        productName: '',
        applicationFeeDescription: '',
        applicationFeePercentage: undefined,
        applicationFeeMinimum: undefined,
        applicationFeeMaximum: undefined,
        loanTenureDescription: '',
        loanTenureMonths: undefined,
        interestRateDescription: '',
        interestRatePercentage: undefined,
        interestRateEffectiveDate: '',
        administrationFeesDescription: '',
        administrationFeesPercentage: undefined,
        serviceFeesDescription: '',
        serviceFeesAmount: undefined,
        minimumPrincipal: undefined,
        maximumPrincipal: undefined,
        startDate: '',
        closeDate: '',
        amortization: '',
        interestMethod: '',
        interestCalculationPeriod: '',
        charges: '',
        otherDetails: '',
        eligibleGender: '',
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    if (!formData.productName || formData.productName.trim() === '') {
      setError('Product name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingProduct && editingProduct.id) {
        await productApi.updateProduct(editingProduct.id, formData as Product);
        setSuccess('Product updated successfully');
      } else {
        await productApi.createProduct(formData as Product);
        setSuccess('Product created successfully');
      }
      
      await loadProducts();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await productApi.deleteProduct(id);
      setSuccess('Product deleted successfully');
      await loadProducts();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value?: number, currency?: string) => {
    if (value == null) return 'N/A';
    const prefix = currency ? `${currency} ` : '$';
    return `${prefix}${value.toLocaleString()}`;
  };

  const renderProductRows = (list: Product[], readOnly: boolean) => {
    if (list.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={readOnly ? 8 : 7} align="center">
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              {readOnly
                ? 'No Skyfin products found. Click Refresh to try again.'
                : 'No local products found. Create one or sync from Skyfin.'}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return list.map((product) => (
      <TableRow key={`${product.source || 'LOCAL'}-${product.fineractProductId || product.id}-${product.productName}`}>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={600}>{product.productName}</Typography>
            {product.shortName && (
              <Typography variant="caption" color="text.secondary">
                {product.shortName}
                {product.fineractProductId != null ? ` · ID ${product.fineractProductId}` : ''}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>{product.currency || 'N/A'}</TableCell>
        <TableCell>
          {product.loanTenureMonths ? `${product.loanTenureMonths} months` : product.loanTenureDescription || 'N/A'}
        </TableCell>
        <TableCell>
          {product.interestRatePercentage != null ? `${product.interestRatePercentage}%` : 'N/A'}
        </TableCell>
        <TableCell>{formatMoney(product.minimumPrincipal, product.currency)}</TableCell>
        <TableCell>{formatMoney(product.maximumPrincipal, product.currency)}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={product.isActive ? 'Active' : 'Inactive'}
              color={product.isActive ? 'success' : 'default'}
              size="small"
            />
            {product.source && (
              <Chip label={product.source} size="small" variant="outlined" color={product.source === 'SKYFIN' ? 'info' : 'default'} />
            )}
            {product.eligibleGender === 'Female' && (
              <Chip label="Women only" size="small" color="secondary" variant="outlined" />
            )}
            {product.eligibleGender === 'Male' && (
              <Chip label="Men only" size="small" color="secondary" variant="outlined" />
            )}
          </Stack>
        </TableCell>
        {!readOnly && (
          <TableCell align="right">
            <IconButton size="small" onClick={() => handleOpenDialog(product)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => product.id && handleDelete(product.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    ));
  };

  const exportProducts = tab === 0 ? products : skyfinProducts;
  const exportSourceLabel = tab === 0 ? 'Local' : 'Skyfin';

  const exportToPDF = () => {
    writeLandscapePdfTable({
      title: `Products Report (${exportSourceLabel})`,
      headers: ['Name', 'Currency', 'Interest %', 'Tenure (mo)', 'Min Principal', 'Max Principal', 'Active'],
      colWidths: [55, 25, 25, 25, 35, 35, 20],
      rows: exportProducts.map((p) => [
        p.productName || 'N/A',
        p.currency || 'N/A',
        p.interestRatePercentage != null ? String(p.interestRatePercentage) : 'N/A',
        p.loanTenureMonths != null ? String(p.loanTenureMonths) : 'N/A',
        p.minimumPrincipal != null ? String(p.minimumPrincipal) : 'N/A',
        p.maximumPrincipal != null ? String(p.maximumPrincipal) : 'N/A',
        p.isActive === false ? 'No' : 'Yes',
      ]),
      fileName: `products-${exportSourceLabel.toLowerCase()}-${todayFileStamp()}.pdf`,
    });
  };

  const exportToExcel = async () => {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Products');

      worksheet.columns = [
        { header: 'ID', key: 'id', width: 10 },
        { header: 'Product Name', key: 'productName', width: 30 },
        { header: 'Short Name', key: 'shortName', width: 14 },
        { header: 'Currency', key: 'currency', width: 10 },
        { header: 'Interest %', key: 'interestRatePercentage', width: 12 },
        { header: 'Tenure Months', key: 'loanTenureMonths', width: 14 },
        { header: 'Min Tenure', key: 'minLoanTenureMonths', width: 12 },
        { header: 'Max Tenure', key: 'maxLoanTenureMonths', width: 12 },
        { header: 'Min Principal', key: 'minimumPrincipal', width: 14 },
        { header: 'Max Principal', key: 'maximumPrincipal', width: 14 },
        { header: 'App Fee %', key: 'applicationFeePercentage', width: 12 },
        { header: 'Admin Fee %', key: 'administrationFeesPercentage', width: 12 },
        { header: 'Service Fee', key: 'serviceFeesAmount', width: 12 },
        { header: 'Eligible Gender', key: 'eligibleGender', width: 14 },
        { header: 'Active', key: 'isActive', width: 10 },
        { header: 'Source', key: 'source', width: 12 },
        { header: 'Fineract Product ID', key: 'fineractProductId', width: 18 },
      ];
      styleExcelHeaderRow(worksheet);

      exportProducts.forEach((p) => {
        worksheet.addRow({
          id: p.id ?? '',
          productName: p.productName || '',
          shortName: p.shortName || '',
          currency: p.currency || '',
          interestRatePercentage: p.interestRatePercentage ?? '',
          loanTenureMonths: p.loanTenureMonths ?? '',
          minLoanTenureMonths: p.minLoanTenureMonths ?? '',
          maxLoanTenureMonths: p.maxLoanTenureMonths ?? '',
          minimumPrincipal: p.minimumPrincipal ?? '',
          maximumPrincipal: p.maximumPrincipal ?? '',
          applicationFeePercentage: p.applicationFeePercentage ?? '',
          administrationFeesPercentage: p.administrationFeesPercentage ?? '',
          serviceFeesAmount: p.serviceFeesAmount ?? '',
          eligibleGender: p.eligibleGender || 'All',
          isActive: p.isActive === false ? 'No' : 'Yes',
          source: p.source || exportSourceLabel,
          fineractProductId: p.fineractProductId ?? '',
        });
      });

      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      styleExcelHeaderRow(metadataSheet);
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Source Tab', value: exportSourceLabel },
        { property: 'Exported Records', value: exportProducts.length },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      await downloadExcelBuffer(
        buffer,
        `products-${exportSourceLabel.toLowerCase()}-${todayFileStamp()}.xlsx`
      );
    } catch (err) {
      console.error('Failed to export Excel:', err);
      alert('Failed to export Excel. Please ensure exceljs is installed.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" component="h2">
          Product Management
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || exportProducts.length === 0}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || exportProducts.length === 0}
            color="success"
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
            onClick={handleSyncFromSkyfin}
            disabled={loading || syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from Skyfin'}
          </Button>
          {tab === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              New Product
            </Button>
          )}
          {tab === 1 && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadSkyfinProducts}
              disabled={loading}
            >
              Refresh Skyfin
            </Button>
          )}
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => {
          setError('');
          setSuccess('');
          setTab(value);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label={`Local (${products.length})`} />
        <Tab label={`Skyfin (${skyfinProducts.length})`} />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {tab === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These products are loaded live from Skyfin (<code>GET /loanproducts</code>).
          Use <strong>Sync from Skyfin</strong> to copy them into the local catalog used by loan applications.
        </Alert>
      )}

      {loading && !(tab === 0 ? products.length : skyfinProducts.length) ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product Name</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Loan Tenure</TableCell>
                <TableCell>Interest Rate</TableCell>
                <TableCell>Min Principal</TableCell>
                <TableCell>Max Principal</TableCell>
                <TableCell>Status</TableCell>
                {tab === 0 && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tab === 0
                ? renderProductRows(products, false)
                : renderProductRows(skyfinProducts, true)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Edit Product' : 'Create New Product'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name *"
                value={formData.productName || ''}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Application Fee
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Application Fee Description"
                value={formData.applicationFeeDescription || ''}
                onChange={(e) => setFormData({ ...formData, applicationFeeDescription: e.target.value })}
                placeholder="e.g., 1% minimum US$20 and a cap of US$100"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Fee Percentage (%)"
                type="number"
                value={formData.applicationFeePercentage || ''}
                onChange={(e) => setFormData({ ...formData, applicationFeePercentage: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Minimum Fee"
                type="number"
                value={formData.applicationFeeMinimum || ''}
                onChange={(e) => setFormData({ ...formData, applicationFeeMinimum: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Maximum Fee"
                type="number"
                value={formData.applicationFeeMaximum || ''}
                onChange={(e) => setFormData({ ...formData, applicationFeeMaximum: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Loan Tenure
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tenure Description"
                value={formData.loanTenureDescription || ''}
                onChange={(e) => setFormData({ ...formData, loanTenureDescription: e.target.value })}
                placeholder="e.g., 3 months maximum"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tenure (Months)"
                type="number"
                value={formData.loanTenureMonths || ''}
                onChange={(e) => setFormData({ ...formData, loanTenureMonths: e.target.value ? parseInt(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency || ''}
                  label="Currency"
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="ZIG">ZIG</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Interest Rate
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interest Rate Description"
                value={formData.interestRateDescription || ''}
                onChange={(e) => setFormData({ ...formData, interestRateDescription: e.target.value })}
                placeholder="e.g., 5% per month Effective 1 August 2024"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Interest Rate (%)"
                type="number"
                value={formData.interestRatePercentage || ''}
                onChange={(e) => setFormData({ ...formData, interestRatePercentage: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Effective Date"
                type="date"
                value={formData.interestRateEffectiveDate || ''}
                onChange={(e) => setFormData({ ...formData, interestRateEffectiveDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Administration Fees
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Administration Fees Description"
                value={formData.administrationFeesDescription || ''}
                onChange={(e) => setFormData({ ...formData, administrationFeesDescription: e.target.value })}
                placeholder="e.g., 10% flat - once off - upfront"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Administration Fees Percentage (%)"
                type="number"
                value={formData.administrationFeesPercentage || ''}
                onChange={(e) => setFormData({ ...formData, administrationFeesPercentage: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Service Fees
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Fees Description"
                value={formData.serviceFeesDescription || ''}
                onChange={(e) => setFormData({ ...formData, serviceFeesDescription: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Fees Amount"
                type="number"
                value={formData.serviceFeesAmount || ''}
                onChange={(e) => setFormData({ ...formData, serviceFeesAmount: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Principal Limits
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Minimum Principal"
                type="number"
                value={formData.minimumPrincipal || ''}
                onChange={(e) => setFormData({ ...formData, minimumPrincipal: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Maximum Principal"
                type="number"
                value={formData.maximumPrincipal || ''}
                onChange={(e) => setFormData({ ...formData, maximumPrincipal: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Dates
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate || ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Close Date"
                type="date"
                value={formData.closeDate || ''}
                onChange={(e) => setFormData({ ...formData, closeDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Interest Configuration
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Amortization</InputLabel>
                <Select
                  value={formData.amortization || ''}
                  label="Amortization"
                  onChange={(e) => setFormData({ ...formData, amortization: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Straight-Line / Linear">Straight-Line / Linear</MenuItem>
                  <MenuItem value="Reducing Balance">Reducing Balance</MenuItem>
                  <MenuItem value="Interest-Only">Interest-Only</MenuItem>
                  <MenuItem value="Bullet / Lump Sum">Bullet / Lump Sum</MenuItem>
                  <MenuItem value="Balloon">Balloon</MenuItem>
                  <MenuItem value="Annuity">Annuity</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Interest Method</InputLabel>
                <Select
                  value={formData.interestMethod || ''}
                  label="Interest Method"
                  onChange={(e) => setFormData({ ...formData, interestMethod: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Flat Rate">Flat Rate</MenuItem>
                  <MenuItem value="Effective Rate / Reducing Balance">Effective Rate / Reducing Balance</MenuItem>
                  <MenuItem value="Daily Rate">Daily Rate</MenuItem>
                  <MenuItem value="Monthly Rate">Monthly Rate</MenuItem>
                  <MenuItem value="Rule of 78">Rule of 78</MenuItem>
                  <MenuItem value="Simple Interest">Simple Interest</MenuItem>
                  <MenuItem value="Compound Interest">Compound Interest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Interest Calculation Period</InputLabel>
                <Select
                  value={formData.interestCalculationPeriod || ''}
                  label="Interest Calculation Period"
                  onChange={(e) => setFormData({ ...formData, interestCalculationPeriod: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                  <MenuItem value="Semi-Monthly">Semi-Monthly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Yearly / Annual">Yearly / Annual</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Eligible Gender</InputLabel>
                <Select
                  value={formData.eligibleGender || ''}
                  label="Eligible Gender"
                  onChange={(e) => setFormData({ ...formData, eligibleGender: e.target.value || null })}
                >
                  <MenuItem value="">All genders</MenuItem>
                  <MenuItem value="Female">Women only</MenuItem>
                  <MenuItem value="Male">Men only</MenuItem>
                </Select>
                <FormHelperText>Leave as All genders unless the product is gender-specific</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                Additional Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Charges"
                value={formData.charges || ''}
                onChange={(e) => setFormData({ ...formData, charges: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other Details"
                value={formData.otherDetails || ''}
                onChange={(e) => setFormData({ ...formData, otherDetails: e.target.value })}
                placeholder="e.g., Penalty fee is annual interest +10%"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive ?? true}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : editingProduct ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
